import { useEffect, useRef, useState, useCallback } from 'react'
import { api } from '../../services/api'

/**
 * Компонент выбора пункта выдачи CDEK.
 * Сначала пользователь выбирает город, потом загружаются ПВЗ только для этого города.
 * Это намного эффективнее, чем загрузка всех 5000+ офисов сразу.
 */
export const PVZSelector = ({ onSelect, defaultCity = null }) => {
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  
  const [citySearch, setCitySearch] = useState(defaultCity || '')
  const [citySuggestions, setCitySuggestions] = useState([])
  const [selectedCity, setSelectedCity] = useState(null)
  const [offices, setOffices] = useState([])
  const [selectedOffice, setSelectedOffice] = useState(null)
  const [loadingOffices, setLoadingOffices] = useState(false)
  const [error, setError] = useState(null)
  const [mapReady, setMapReady] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [showOfficeList, setShowOfficeList] = useState(false)
  
  const debounceRef = useRef(null)

  // Загрузка Яндекс.Карт (проверяем чтобы не загрузить дважды)
  useEffect(() => {
    const loadYandexMaps = () => {
      // Если ymaps уже есть и готов
      if (window.ymaps && window.ymaps.Map) {
        setMapReady(true)
        return
      }
      
      // Если ymaps загружается, ждём ready
      if (window.ymaps) {
        window.ymaps.ready(() => setMapReady(true))
        return
      }
      
      // Проверяем, не загружается ли уже скрипт
      const existingScript = document.querySelector('script[src*="api-maps.yandex.ru"]')
      if (existingScript) {
        // Скрипт уже добавлен, ждём загрузки
        const checkReady = setInterval(() => {
          if (window.ymaps) {
            clearInterval(checkReady)
            window.ymaps.ready(() => setMapReady(true))
          }
        }, 100)
        setTimeout(() => clearInterval(checkReady), 10000) // timeout 10s
        return
      }
      
      const apiKey = 'aa5f6549-a32e-41ca-b74e-a45aca6b41d1'
      const script = document.createElement('script')
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU`
      script.async = true
      script.onload = () => {
        window.ymaps.ready(() => setMapReady(true))
      }
      script.onerror = () => setError('Не удалось загрузить карты')
      document.head.appendChild(script)
    }
    
    loadYandexMaps()
  }, [])

  // Автокомплит городов
  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setCitySuggestions([])
      return
    }
    
    try {
      const response = await api.get('/delivery/suggest-city/', {
        params: { name: query }
      })
      setCitySuggestions(response.data || [])
      setShowSuggestions(true)
    } catch {
      setCitySuggestions([])
    }
  }, [])

  // Debounced поиск городов
  const handleCityInputChange = (e) => {
    const value = e.target.value
    setCitySearch(value)
    setSelectedCity(null)
    setOffices([])
    setSelectedOffice(null)
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    
    debounceRef.current = setTimeout(() => {
      searchCities(value)
    }, 300)
  }

  // Выбор города из списка
  const handleCitySelect = async (city) => {
    setSelectedCity(city)
    setCitySearch(city.full_name || city.city)
    setShowSuggestions(false)
    setCitySuggestions([])
    
    // Загружаем офисы для выбранного города
    setLoadingOffices(true)
    setError(null)
    
    try {
      const response = await api.get('/delivery/get-office-list/', {
        params: { city_code: city.code }
      })
      
      const officeList = response.data || []
      setOffices(officeList)
      
      if (officeList.length === 0) {
        setError('В этом городе нет пунктов выдачи CDEK')
      }
      // Карта будет инициализирована через useEffect когда DOM обновится
    } catch {
      setError('Ошибка загрузки пунктов выдачи')
    } finally {
      setLoadingOffices(false)
    }
  }
  
  // Выбор офиса (определяем перед initOrUpdateMap, т.к. он используется там)
  const handleOfficeSelect = useCallback((office) => {
    setSelectedOffice(office)
    
    const pvzData = {
      code: office.code,
      name: office.name,
      address: office.address,
      city: office.city || selectedCity?.city || '',
      latitude: office.lat,
      longitude: office.lon,
      work_time: office.work_time_str || '',
      phone: office.phones?.[0]?.number || '',
      city_code: office.city_code || selectedCity?.code,
      type: office.type || 'PVZ',
    }
    
    if (onSelect) {
      onSelect(pvzData)
    }
  }, [selectedCity, onSelect])

  // Инициализация или обновление карты
  const initOrUpdateMap = useCallback((city, officeList) => {
    if (!window.ymaps || !mapContainerRef.current) {
      return
    }
    
    const centerLat = city.latitude || 55.75
    const centerLon = city.longitude || 37.62
    
    // Уничтожаем старую карту если есть
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.destroy()
      } catch {
        // Ignore destroy errors
      }
      mapInstanceRef.current = null
    }
    
    // Очищаем контейнер
    mapContainerRef.current.innerHTML = ''
    
    // Создаём новую карту через ymaps.ready чтобы гарантировать готовность
    window.ymaps.ready(() => {
      try {
        mapInstanceRef.current = new window.ymaps.Map(mapContainerRef.current, {
          center: [centerLat, centerLon],
          zoom: 11,
          controls: ['zoomControl', 'geolocationControl']
        })
        
        // Добавляем маркеры офисов
        markersRef.current = []
        
        officeList.forEach((office) => {
          if (!office.lat || !office.lon) return
          
          const placemark = new window.ymaps.Placemark(
            [office.lat, office.lon],
            {
              balloonContentHeader: `<strong>${office.code}</strong>`,
              balloonContentBody: `
                <div style="font-family: 'Inter', sans-serif; font-size: 12px; max-width: 250px;">
                  <div><strong>${office.address}</strong></div>
                  <div style="color: #666; margin-top: 4px;">${office.work_time_str || ''}</div>
                  <button onclick="window.selectPVZ('${office.code}')" 
                    style="margin-top: 8px; padding: 6px 12px; background: #000; color: #fff; border: none; cursor: pointer; font-family: 'Inter', sans-serif; width: 100%; text-transform: uppercase;">
                    ВЫБРАТЬ
                  </button>
                </div>
              `,
              hintContent: office.code
            },
            {
              preset: 'islands#blackCircleDotIcon', // Changed to black to match theme
              iconColor: '#000000'
            }
          )
          
          mapInstanceRef.current.geoObjects.add(placemark)
          markersRef.current.push({ placemark, office })
        })
        
        // Глобальная функция для выбора ПВЗ из balloon
        window.selectPVZ = (code) => {
          const found = officeList.find(o => o.code === code)
          if (found) {
            handleOfficeSelect(found)
          }
        }
        
        // Автоматически подгоняем zoom чтобы все точки были видны
        if (officeList.length > 0 && mapInstanceRef.current.geoObjects.getBounds()) {
          mapInstanceRef.current.setBounds(
            mapInstanceRef.current.geoObjects.getBounds(),
            { checkZoomRange: true, zoomMargin: 40 }
          )
        }
      } catch {
        setError('Ошибка инициализации карты')
      }
    })
  }, [handleOfficeSelect])

  // Инициализация карты когда все условия выполнены (после рендера контейнера)
  useEffect(() => {
    if (mapReady && selectedCity && offices.length > 0 && mapContainerRef.current) {
      initOrUpdateMap(selectedCity, offices)
    }
  }, [mapReady, selectedCity, offices, initOrUpdateMap])

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy()
        mapInstanceRef.current = null
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      delete window.selectPVZ
    }
  }, [])

  return (
    <div className="pvz-selector">
      {/* Поиск города */}
      <div style={{ marginBottom: '16px', position: 'relative' }}>
        <label className="pvz-label">
          Город доставки
        </label>
        <input
          type="text"
          value={citySearch}
          onChange={handleCityInputChange}
          onFocus={() => citySuggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Начните вводить название города..."
          className="pvz-input"
        />
        
        {/* Выпадающий список городов */}
        {showSuggestions && citySuggestions.length > 0 && (
          <div className="pvz-suggestions">
            {citySuggestions.map((city, idx) => (
              <div
                key={city.code || idx}
                onClick={() => handleCitySelect(city)}
                className="pvz-suggestion-item"
              >
                {city.full_name || city.city}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Индикатор загрузки офисов */}
      {loadingOffices && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          Загрузка пунктов выдачи...
        </div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="checkout-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Карта (показывается только после выбора города) */}
      {selectedCity && offices.length > 0 && (
        <>
          <div style={{ 
            fontSize: '10px', 
            marginBottom: '8px',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Найдено пунктов выдачи: {offices.length}
          </div>
          
          <div
            ref={mapContainerRef}
            className="pvz-map-container"
          />
        </>
      )}

      {/* Список офисов (сворачиваемый) */}
      {selectedCity && offices.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <button
            type="button"
            onClick={() => setShowOfficeList(!showOfficeList)}
            className="pvz-list-toggle"
          >
            <span>Показать список всех пунктов ({offices.length})</span>
            <span style={{ 
              transform: showOfficeList ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </button>
          
          {showOfficeList && (
            <div className="pvz-list">
              {offices.map((office, idx) => (
                <div
                  key={office.code || idx}
                  onClick={() => {
                    handleOfficeSelect(office)
                    setShowOfficeList(false)
                  }}
                  className="pvz-list-item"
                  style={{
                    background: selectedOffice?.code === office.code ? '#f9f9f9' : '#fff'
                  }}
                >
                  <div style={{ fontWeight: '500', fontSize: '11px', marginBottom: '2px' }}>
                    {office.code}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>
                    {office.address}
                  </div>
                  {office.work_time_str && (
                    <div style={{ fontSize: '9px', color: '#999', marginTop: '4px' }}>
                      {office.work_time_str}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Выбранный ПВЗ */}
      {selectedOffice && (
        <div className="pvz-selected">
          <div className="pvz-selected-info">
            <div className="pvz-selected-code">{selectedOffice.code}</div>
            <div className="pvz-selected-address">{selectedOffice.address}</div>
          {selectedOffice.work_time_str && (
              <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              {selectedOffice.work_time_str}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Подсказка если город не выбран */}
      {!selectedCity && !loadingOffices && (
        <div className="empty-state-mini">
          Введите название города для отображения пунктов выдачи
        </div>
      )}
    </div>
  )
}
